import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Button,
  Paper,
  Chip,
  ImageList,
  ImageListItem,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore,
  NavigateNext,
  ZoomIn,
  Share,
  Download,
  PhotoLibrary,
  Event as EventIcon,
  DateRange as TripIcon,
  Info as InfoIcon
} from '@mui/icons-material';

// Define the Photo type if not imported
interface Photo {
  id: string;
  url: string;
  thumbnailUrl?: string;
  title?: string;
  description?: string;
  takenAt: Date;
  location?: {
    name?: string;
    latitude?: number;
    longitude?: number;
  };
  userId?: string;
  eventId?: string;
  tripId?: string;
}

interface PhotoPopupProps {
  open: boolean;
  onClose: () => void;
  photos: Photo[];
  selectedDay: string;
  onCreateEvent?: (photoIds: string[]) => void;
  onCreateTrip?: (photoIds: string[]) => void;
}

const PhotoPopup: React.FC<PhotoPopupProps> = ({
  open,
  onClose,
  photos,
  selectedDay,
  onCreateEvent,
  onCreateTrip
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'single'>('grid');
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  
  // Get the current photo
  const currentPhoto = photos[currentPhotoIndex];
  
  // Handle navigation to previous photo
  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };
  
  // Handle navigation to next photo
  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };
  
  // Toggle photo selection
  const togglePhotoSelection = (photoId: string) => {
    if (selectedPhotos.includes(photoId)) {
      setSelectedPhotos(selectedPhotos.filter(id => id !== photoId));
    } else {
      setSelectedPhotos([...selectedPhotos, photoId]);
    }
  };
  
  // Handle view mode change
  const switchToSingleView = (index: number) => {
    setCurrentPhotoIndex(index);
    setViewMode('single');
  };
  
  // Format date from the photo's takenAt field
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  return (
     <Dialog
        open={open}
        onClose={onClose}
        maxWidth='lg'
        fullWidth
        PaperProps={{
           sx: { height: viewMode === 'single' ? '90vh' : '80vh' },
        }}
     >
        <DialogTitle
           sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}
        >
           <Box>
              <Typography variant='h6' component='div'>
                 Photos from {selectedDay}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                 {photos.length} photos available
              </Typography>
           </Box>
           <Box>
              <Button
                 variant='outlined'
                 size='small'
                 startIcon={<PhotoLibrary />}
                 onClick={() => setViewMode(viewMode === 'grid' ? 'single' : 'grid')}
                 sx={{ mr: 1 }}
              >
                 {viewMode === 'grid' ? 'Single View' : 'Grid View'}
              </Button>
              {viewMode === 'grid' && selectedPhotos.length > 0 && (
                 <>
                    {onCreateEvent && (
                       <Button
                          variant='outlined'
                          size='small'
                          startIcon={<EventIcon />}
                          onClick={() => onCreateEvent(selectedPhotos)}
                          sx={{ mr: 1 }}
                       >
                          Create Event
                       </Button>
                    )}
                    {onCreateTrip && (
                       <Button
                          variant='outlined'
                          size='small'
                          startIcon={<TripIcon />}
                          onClick={() => onCreateTrip(selectedPhotos)}
                          sx={{ mr: 1 }}
                       >
                          Create Trip
                       </Button>
                    )}
                 </>
              )}
              <IconButton onClick={onClose} size='small'>
                 <CloseIcon />
              </IconButton>
           </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 2 }}>
           {photos.length === 0 ? (
              <Box
                 sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                 }}
              >
                 <Typography variant='h6' color='text.secondary'>
                    No photos available for this day
                 </Typography>
              </Box>
           ) : viewMode === 'grid' ? (
              // Grid View
              <>
                 <Box sx={{ mb: 2 }}>
                    <Chip
                       label={`${selectedPhotos.length} photos selected`}
                       color={selectedPhotos.length > 0 ? 'primary' : 'default'}
                       sx={{ mr: 1 }}
                    />
                    {selectedPhotos.length > 0 && (
                       <Button size='small' variant='text' onClick={() => setSelectedPhotos([])}>
                          Clear Selection
                       </Button>
                    )}
                 </Box>

                 <ImageList cols={4} gap={8} sx={{ height: 'calc(100% - 48px)', overflow: 'auto' }}>
                    {photos.map((photo, index) => (
                       <ImageListItem
                          key={photo.id}
                          onClick={() => switchToSingleView(index)}
                          sx={{
                             cursor: 'pointer',
                             border: selectedPhotos.includes(photo.id)
                                ? '2px solid #1976d2'
                                : 'none',
                             borderRadius: 1,

                             position: 'relative',
                             width: '100%',
                             height: 400,
                          }}
                       >
                          <img
                             src={photo.thumbnailUrl || photo.url}
                             alt={photo.title || `Photo ${index + 1}`}
                             loading='lazy'
                             style={{ height: '200px', objectFit: 'cover' }}
                          />
                          <Box
                             sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                p: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                             }}
                          >
                             <Typography
                                variant='caption'
                                sx={{ color: 'white', fontSize: '0.7rem' }}
                             >
                                {formatDate(photo.takenAt)}
                             </Typography>
                             <IconButton
                                size='small'
                                sx={{ color: 'white', p: 0.5 }}
                                onClick={(e) => {
                                   e.stopPropagation();
                                   togglePhotoSelection(photo.id);
                                }}
                             >
                                <InfoIcon fontSize='small' />
                             </IconButton>
                          </Box>
                       </ImageListItem>
                    ))}
                 </ImageList>
              </>
           ) : (
              // Single Photo View
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Button
                       startIcon={<NavigateBefore />}
                       onClick={handlePrevPhoto}
                       variant='outlined'
                       size='small'
                    >
                       Previous
                    </Button>
                    <Typography>
                       Photo {currentPhotoIndex + 1} of {photos.length}
                    </Typography>
                    <Button
                       endIcon={<NavigateNext />}
                       onClick={handleNextPhoto}
                       variant='outlined'
                       size='small'
                    >
                       Next
                    </Button>
                 </Box>

                 <Box
                    sx={{
                       flexGrow: 1,
                       display: 'flex',
                       justifyContent: 'center',
                       alignItems: 'center',
                       overflow: 'hidden',
                       position: 'relative',
                       width: '100%',
                       height: 400,
                       background: `url(${currentPhoto.url}) `,
                       objectFit: 'scale-down',
                       backgroundPosition: 'center',
                       backgroundRepeat: 'no-repeat',
                    }}
                 >
                    {/* {currentPhoto ? (
                    <img
                      
                          src={currentPhoto.url}
                          alt={currentPhoto.title || `Photo ${currentPhotoIndex + 1}`}
                          style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                       />
                    ) : (
                       <CircularProgress />
                    )} */}
                 </Box>

                 <Paper sx={{ mt: 2, p: 2 }}>
                    <Box
                       sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                       }}
                    >
                       <Typography variant='subtitle1'>
                          {currentPhoto?.title || `Photo ${currentPhotoIndex + 1}`}
                       </Typography>
                       {/* <Box>
                          <IconButton size='small' sx={{ mr: 1 }}>
                             <ZoomIn />
                          </IconButton>
                          <IconButton size='small' sx={{ mr: 1 }}>
                             <Share />
                          </IconButton>
                          <IconButton size='small'>
                             <Download />
                          </IconButton>
                       </Box> */}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Grid container spacing={2}>
                       <Grid item xs={6}>
                          <Typography variant='body2' color='text.secondary'>
                             Date Taken:
                          </Typography>
                          <Typography variant='body2'>
                             {currentPhoto && formatDate(currentPhoto.takenAt)}
                          </Typography>
                       </Grid>
                       <Grid item xs={6}>
                          <Typography variant='body2' color='text.secondary'>
                             Location:
                          </Typography>
                          <Typography variant='body2'>
                             {currentPhoto?.location?.name || 'Unknown location'}
                          </Typography>
                       </Grid>
                       {currentPhoto?.description && (
                          <Grid item xs={12}>
                             <Typography variant='body2' color='text.secondary'>
                                Description:
                             </Typography>
                             <Typography variant='body2'>{currentPhoto.description}</Typography>
                          </Grid>
                       )}
                    </Grid>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                       {currentPhoto?.eventId && (
                          <Chip
                             icon={<EventIcon />}
                             label='Part of an Event'
                             size='small'
                             color='primary'
                          />
                       )}
                       {currentPhoto?.tripId && (
                          <Chip
                             icon={<TripIcon />}
                             label='Part of a Trip'
                             size='small'
                             color='secondary'
                          />
                       )}
                    </Box>
                 </Paper>
              </Box>
           )}
        </DialogContent>
     </Dialog>
  );
};

export default PhotoPopup;